import { useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation, Trans } from 'react-i18next'
import Box from '@mui/material/Box'
import { loadScript } from '../../../utils/scriptLoader.js'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import Container from '@mui/material/Container'
import './Registration.css'
import Button from '@mui/material/Button'

const NoResultsMessage = ({ notStarted }) => {
  const { t } = useTranslation('race')
  return (
    <Box role='alert'>
      <Typography sx={{ mb: 2 }} variant='h6'>
        {notStarted
          ? t('live.notStarted')
          : t('live.ended', { tab: t('tabs.results') })}
      </Typography>
    </Box>
  )
}

const initializeLive = raceId => {
  return new Promise((resolve, reject) => {
    const container = document.getElementById('divRRPublish')

    if (!container) {
      reject(new Error('Container not found'))
      return
    }

    if (container.dataset.initialized) {
      resolve()
      return
    }

    loadScript('/libs/mrr-publish.js', 'mrr-publish')
      .then(() => {
        // Check if functions are available
        if (typeof window.RRPublish === 'function') {
          new window.RRPublish(container, raceId, 'live')
            .then(rrp => {
              rrp.ShowInfoText = false
              container.dataset.initialized = 'true'
              resolve(rrp)
            })
            .catch(error => {
              reject(new Error('RRPublish instance creation failed: ' + error.message))
            })
        } else {
          // If RRPublish isn't available, check for initialization function
          if (typeof window.initMRR === 'function') {
            try {
              window.initMRR()

              if (typeof window.RRPublish === 'function') {
                new window.RRPublish(container, raceId, 'live')
                  .then(rrp => {
                    rrp.ShowInfoText = false
                    container.dataset.initialized = 'true'
                    resolve(rrp)
                  })
                  .catch(error => {
                    reject(new Error('RRPublish initialization failed: ' + error.message))
                  })
              } else {
                reject(new Error('RRPublish initialization failed: RRPublish not available after initMRR'))
              }
            } catch (error) {
              reject(new Error('initMRR failed: ' + error.message))
            }
          } else {
            // FIX: If neither function is available, force reload the script
            // Remove existing script
            const existingScript = document.querySelectorAll('script[id="mrr-publish"]')[0]
            if (existingScript) {
              existingScript.remove()
            }

            // Load script again
            return loadScript('/libs/mrr-publish.js', 'mrr-publish')
              .then(() => {
                // Try again after reload
                if (typeof window.RRPublish === 'function') {
                  new window.RRPublish(container, raceId, 'live')
                    .then(rrp => {
                      rrp.ShowInfoText = false
                      container.dataset.initialized = 'true'
                      resolve(rrp)
                    })
                    .catch(error => {
                      reject(new Error('RRPublish initialization failed: ' + error.message))
                    })
                } else if (typeof window.initMRR === 'function') {
                  window.initMRR()
                  new window.RRPublish(container, raceId, 'live')
                    .then(rrp => {
                      rrp.ShowInfoText = false
                      container.dataset.initialized = 'true'
                      resolve(rrp)
                    })
                    .catch(error => {
                      reject(new Error('RRPublish initialization failed: ' + error.message))
                    })
                } else {
                  reject(new Error('RRPublish initialization failed: Functions not available after script reload'))
                }
              })
              .catch(reloadError => {
                reject(new Error('Script reload failed: ' + reloadError.message))
              })
          }
        }
      })
      .catch(error => {
        reject(new Error('Script loading failed: ' + error.message))
      })
  })
}

export default function LiveTime ({ eventDetails, isTabDisabled, today }) {
  const { t } = useTranslation('race')
  const resultsRef = useRef(null)
  const lastInitializedRaceId = useRef(null)
  const { myRaceResultsId } = eventDetails || {}
  // Memoize initialization function to prevent recreation on every render
  const initLive = useCallback(() => {
    if (myRaceResultsId && resultsRef.current) {
      initializeLive(myRaceResultsId, resultsRef.current)
        .catch(error => console.error('Registration init failed:', error))
    }
  }, [myRaceResultsId])

  // Registration initialization effect
  useEffect(() => {
    if (myRaceResultsId !== lastInitializedRaceId.current &&
        eventDetails.showLive !== false && !isTabDisabled('live')) {
      lastInitializedRaceId.current = myRaceResultsId
      if (resultsRef.current) resultsRef.current.dataset.initialized = ''
      initLive()
    }
  }, [myRaceResultsId, eventDetails.status, eventDetails.registrationsClosed, initLive])

  if (isTabDisabled('live')) {
    return (
      <Container maxWidth='xl'>
        <NoResultsMessage notStarted={today.isBefore(eventDetails.firstEventDate.startOf('day'))} />
      </Container>

    )
  }

  // if (now.isBefore(eventDetails.firstEventDate.startOf('day')) || eventDetails.hideLive) {
  //   return <NoResultsMessage notStarted={now.isBefore(eventDetails.firstEventDate.startOf('day'))} />
  // }

  if (myRaceResultsId === '366560') {
    return (
      <Container maxWidth='xl' sx={{ overflow: 'auto' }} role='main' sx={{ textAlign: 'center' }}>
        <Button variant='contained' href='https://goat.v3.livetrail.net/en/2026' sx={{ mt: 4 }} target='_blank' rel='noopener noreferrer'>
          {t('live.viewLiveResults')}
        </Button>
        <Typography sx={{ mt: 2 }}>
          {t('live.redirect')}
        </Typography>
      </Container>
    )
  }

  return (
    <Container maxWidth='xl' sx={{ overflow: 'auto' }} role='main'>
      <Typography sx={{ mb: 2 }}>
        <Trans
          i18nKey='live.embedNote'
          ns='race'
          components={{
            1: <Link
              href={`https://my.raceresult.com/${myRaceResultsId}/live`}
              target='_blank'
              rel='noopener noreferrer'
              sx={{ textDecoration: 'underline' }}
               />
          }}
        />
      </Typography>
      <Box ref={resultsRef} id='divRRPublish' className='RRPublish' />
    </Container>
  )
}

LiveTime.propTypes = {
  eventDetails: PropTypes.shape({
    myRaceResultsId: PropTypes.string,
    firstEventDate: PropTypes.object,
    startTimes: PropTypes.object,
    regCloseHoursBeforeStart: PropTypes.number,
    status: PropTypes.oneOf(['open', 'soon', 'later']),
    registrationsClosed: PropTypes.bool
  })
}
